import { useMemo, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render?: (row: T) => ReactNode;
  value?: (row: T) => string | number | null | undefined; // for sorting/searching
  sortable?: boolean;
}

interface DataTableProps<T> {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  actions?: ReactNode;
  defaultSort?: string;
}

/** The sortable/searchable value of a cell: the column's own accessor, else
 *  the raw field. Pure, so it lives outside the component and stays out of the
 *  memo's dependencies. */
function cellValue<T>(row: T, col: Column<T>): string | number | null | undefined {
  return col.value ? col.value(row) : (row as Record<string, unknown>)[col.key] as string | number;
}

export function DataTable<T>({
  rows, columns, rowKey, onRowClick, empty, searchable = true,
  searchPlaceholder = 'Search…', actions, defaultSort,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSort);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = useMemo(() => {
    if (!rows) return undefined;
    let out = rows;
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => String(cellValue(r, c) ?? '').toLowerCase().includes(q)),
      );
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col) {
        out = [...out].sort((a, b) => {
          const av = cellValue(a, col);
          const bv = cellValue(b, col);
          if (av == null) return 1;
          if (bv == null) return -1;
          const cmp =
            typeof av === 'number' && typeof bv === 'number'
              ? av - bv
              : String(av).localeCompare(String(bv), 'en-ZA', { numeric: true });
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [rows, query, sortKey, sortDir, columns]);

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  if (!rows) {
    return <div className="centered" style={{ padding: 'var(--sp-6)' }}><span className="spinner" /></div>;
  }

  return (
    <div className="table-wrap">
      {(searchable || actions) && (
        <div className="table-toolbar">
          {searchable && (
            <input
              className="table-search"
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <span className="spacer" />
          {actions}
        </div>
      )}

      <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ textAlign: c.align ?? 'left', width: c.width }}
                  className={c.sortable !== false ? 'is-sortable' : undefined}
                  onClick={c.sortable !== false ? () => toggleSort(c.key) : undefined}
                >
                  {c.label}
                  {sortKey === c.key && <span className="table__sort">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered?.length ? (
              filtered.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'is-clickable' : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                      {c.render ? c.render(row) : (cellValue(row, c) ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className="placeholder" style={{ border: 'none', padding: 'var(--sp-5)' }}>
                    {query ? `Nothing matches “${query}”.` : empty ?? 'No records yet.'}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered && rows.length > 0 && (
        <div className="table-footer small muted">
          {filtered.length === rows.length
            ? `${rows.length} record${rows.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${rows.length} records`}
        </div>
      )}
    </div>
  );
}
